import { create } from 'zustand';
import type { Group, GroupMember, GroupMessage, GroupStats, PaginatedGroupsResponse } from '../types';

interface GroupState {
  // Data
  groups: Group[];
  myGroups: Group[];
  currentGroup: Group | null;
  groupMembers: Record<number, GroupMember[]>;
  groupMessages: Record<number, GroupMessage[]>;
  groupStats: GroupStats | null;
  
  // UI State
  isLoading: boolean;
  isLoadingMembers: boolean;
  isLoadingMessages: boolean;
  error: string | null;
  
  // Pagination
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;
  currentPage: number;
}

interface GroupActions {
  // Groups
  setGroups: (response: PaginatedGroupsResponse) => void;
  setMyGroups: (response: PaginatedGroupsResponse) => void;
  setCurrentGroup: (group: Group | null) => void;
  addGroup: (group: Group) => void;
  updateGroup: (groupId: number, updates: Partial<Group>) => void;
  removeGroup: (groupId: number) => void;
  incrementGroupUnread: (groupId: number) => void;
  clearGroupUnread: (groupId: number) => void;

  // Members
  setGroupMembers: (groupId: number, members: GroupMember[]) => void;
  addGroupMember: (groupId: number, member: GroupMember) => void;
  updateGroupMember: (groupId: number, memberId: number, updates: Partial<GroupMember>) => void;
  removeGroupMember: (groupId: number, memberId: number) => void;
  
  // Messages
  setGroupMessages: (groupId: number, messages: GroupMessage[]) => void;
  addGroupMessage: (groupId: number, message: GroupMessage) => void;
  prependGroupMessages: (groupId: number, messages: GroupMessage[]) => void;
  
  // Stats
  setGroupStats: (stats: GroupStats) => void;
  
  // UI State
  setLoading: (loading: boolean) => void;
  setLoadingMembers: (loading: boolean) => void;
  setLoadingMessages: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  
  // Pagination
  setCurrentPage: (page: number) => void;
  
  // Actions
  clearGroups: () => void;
  clearCurrentGroup: () => void;
  clearGroupMessages: (groupId: number) => void;
}

type GroupStore = GroupState & GroupActions;

const initialState: GroupState = {
  groups: [],
  myGroups: [],
  currentGroup: null,
  groupMembers: {},
  groupMessages: {},
  groupStats: null,
  isLoading: false,
  isLoadingMembers: false,
  isLoadingMessages: false,
  error: null,
  pagination: null,
  currentPage: 1,
};

export const useGroupStore = create<GroupStore>((set) => ({
  ...initialState,
  
  // Groups
  setGroups: (response) => {
    set({
      groups: response.groups,
      pagination: response.pagination,
      isLoading: false,
      error: null,
    });
  },
  
  setMyGroups: (response) => {
    set({
      myGroups: response.groups,
      isLoading: false,
      error: null,
    });
  },
  
  setCurrentGroup: (group) => {
    set({ currentGroup: group, isLoading: false, error: null });
  },
  
  addGroup: (group) => {
    set((state) => ({
      groups: [group, ...state.groups],
      myGroups: [...state.myGroups, group],
    }));
  },
  
  updateGroup: (groupId, updates) => {
    set((state) => ({
      groups: state.groups.map(group => 
        group.id === groupId ? { ...group, ...updates } : group
      ),
      myGroups: state.myGroups.map(group => 
        group.id === groupId ? { ...group, ...updates } : group
      ),
      currentGroup: state.currentGroup?.id === groupId 
        ? { ...state.currentGroup, ...updates }
        : state.currentGroup,
    }));
  },
  
  removeGroup: (groupId) => {
    set((state) => ({
      groups: state.groups.filter(group => group.id !== groupId),
      myGroups: state.myGroups.filter(group => group.id !== groupId),
      currentGroup: state.currentGroup?.id === groupId ? null : state.currentGroup,
    }));
  },

  incrementGroupUnread: (groupId) => {
    set((state) => ({
      myGroups: state.myGroups.map(group =>
        group.id === groupId
          ? { ...group, unreadCount: (group.unreadCount ?? 0) + 1 }
          : group
      ),
    }));
  },

  clearGroupUnread: (groupId) => {
    set((state) => ({
      myGroups: state.myGroups.map(group =>
        group.id === groupId ? { ...group, unreadCount: 0 } : group
      ),
    }));
  },
  
  // Members
  setGroupMembers: (groupId, members) => {
    set((state) => ({
      groupMembers: {
        ...state.groupMembers,
        [groupId]: members,
      },
      isLoadingMembers: false,
    }));
  },
  
  addGroupMember: (groupId, member) => {
    set((state) => ({
      groupMembers: {
        ...state.groupMembers,
        [groupId]: [...(state.groupMembers[groupId] || []), member],
      },
    }));
  },
  
  updateGroupMember: (groupId, memberId, updates) => {
    set((state) => ({
      groupMembers: {
        ...state.groupMembers,
        [groupId]: (state.groupMembers[groupId] || []).map(member =>
          member.userId === memberId ? { ...member, ...updates } : member
        ),
      },
    }));
  },
  
  removeGroupMember: (groupId, memberId) => {
    set((state) => ({
      groupMembers: {
        ...state.groupMembers,
        [groupId]: (state.groupMembers[groupId] || []).filter(member => 
          member.userId !== memberId
        ),
      },
    }));
  },
  
  // Messages
  setGroupMessages: (groupId, messages) => {
    set((state) => ({
      groupMessages: {
        ...state.groupMessages,
        [groupId]: messages,
      },
      isLoadingMessages: false,
    }));
  },
  
  addGroupMessage: (groupId, message) => {
    set((state) => ({
      groupMessages: {
        ...state.groupMessages,
        [groupId]: [...(state.groupMessages[groupId] || []), message],
      },
    }));
  },
  
  prependGroupMessages: (groupId, messages) => {
    set((state) => ({
      groupMessages: {
        ...state.groupMessages,
        [groupId]: [...messages, ...(state.groupMessages[groupId] || [])],
      },
    }));
  },
  
  // Stats
  setGroupStats: (stats) => {
    set({ groupStats: stats });
  },
  
  // UI State
  setLoading: (loading) => {
    set({ isLoading: loading });
  },
  
  setLoadingMembers: (loading) => {
    set({ isLoadingMembers: loading });
  },
  
  setLoadingMessages: (loading) => {
    set({ isLoadingMessages: loading });
  },
  
  setError: (error) => {
    set({ error, isLoading: false });
  },
  
  clearError: () => {
    set({ error: null });
  },
  
  // Pagination
  setCurrentPage: (page) => {
    set({ currentPage: page });
  },
  
  // Actions
  clearGroups: () => {
    set({
      groups: [],
      pagination: null,
      currentPage: 1,
    });
  },
  
  clearCurrentGroup: () => {
    set({ currentGroup: null });
  },
  
  clearGroupMessages: (groupId) => {
    set((state) => ({
      groupMessages: {
        ...state.groupMessages,
        [groupId]: [],
      },
    }));
  },
}));